import h from 'hyperscript';

import { uniqueId } from '@disy/lib/unique-id';

import { createAutoToggle } from 'ui/auto-toggle/auto-toggle';
import { createNameInput } from 'ui/name-input/name-input';

const COMPONENT_NAME = 'd-editable-name-panel';

export class EditableNamePanel extends HTMLElement {
  readonly #autoToggle;
  readonly #nameInput;

  constructor(
    title: string,
    {
      showAutoToggle = true,
      disabled = false,
      name,
      required = true,
    }: {
      showAutoToggle?: boolean;
      disabled?: boolean;
      name?: string;
      required?: boolean;
    } = {},
  ) {
    super();
    this.#nameInput = this.#createNameInput({ name, required });

    const label = h('label', { id: uniqueId(), htmlFor: this.#nameInput.id }, title);

    if (showAutoToggle) {
      this.#autoToggle = this.#createAutoToggle();
      this.#autoToggle.setAttribute('aria-labelledby', label.id);
      this.append(this.#autoToggle);
    }

    this.classList.add(COMPONENT_NAME, 'form-group');
    this.append(label, this.#nameInput);
    this.disabled = disabled;
  }

  get name() {
    return this.#nameInput.value;
  }

  get isAutoEnabled() {
    return this.#autoToggle?.pressed || false;
  }

  update({ name, defaultName }: { name: string; defaultName?: string }) {
    if (this.#autoToggle) {
      this.#autoToggle.pressed = !defaultName;
    }
    this.#updateInternal();
    this.#nameInput.value = name;
    this.#nameInput.defaultValue = name;
  }

  get disabled() {
    return this.classList.contains('is-disabled');
  }

  set disabled(disabled: boolean) {
    this.classList.toggle('is-disabled', disabled);
    this.#updateInternal();
  }

  #createAutoToggle() {
    return createAutoToggle({
      styleClass: 'float-right',
      onclick: () => {
        this.#updateInternal();
        this.dispatchEvent(new CustomEvent('change:auto'));
      },
    });
  }

  #updateInternal() {
    const disabled = this.disabled;
    this.#nameInput.disabled = disabled;

    if (this.#autoToggle) {
      this.#autoToggle.disabled = disabled;
    }
  }

  #createNameInput({ name, required }: { name?: string; required: boolean }) {
    return createNameInput({
      id: uniqueId(),
      ...(name && { name }),
      required,
      className: 'form-control',
      onchange: () => this.#onChangeName(),
    });
  }

  #onChangeName() {
    // Switch to manual mode
    if (this.#autoToggle) {
      this.#autoToggle.pressed = false;
    }

    const input = this.#nameInput;
    if (!this.disabled && input.defaultValue !== input.value) {
      input.defaultValue = input.value;
      this.dispatchEvent(new CustomEvent('change:name'));
    }
  }
}

customElements.define(COMPONENT_NAME, EditableNamePanel);
